import React, { useRef, useState } from 'react'
import moment from 'moment'
import { Modal, ModalHeader, ModalBody } from 'reactstrap'
import {
  BsDownload,
  BsEmojiFrown,
  BsEmojiSmile,
  BsEmojiNeutral,
  BsFileEarmarkPdf,
  BsPaperclip,
  BsBoxArrowUpRight,
} from 'react-icons/bs'
import { FaRegFaceMehBlank } from 'react-icons/fa6'
import TriviaSMSResponses from './TriviaSMSResponses'
import OoPopover from '../../../../components/UI/Popover/OoPopover'
import { profilePicValidityCheck } from '../../../../utils/constants'
import {
  downloadElementAsPdf,
  toPdfFileName,
} from '../../../../utils/downloadElementAsPdf'
import { SENTIMENTS_TYPO_MAP2 } from '../../data'
import ListEmptyDash from '../ListEmptyDash'
import './MessagingResponseFullModal.css'
import '../../trivvy-response-modal-bwai.css'

// Shared response viewer extracted from
// MessagingTriviaResultsResponses.jsx (the legacy `fullTextModal` block).
// Behavior is preserved verbatim — only the surrounding state plumbing was
// hoisted to a prop. The setter previously wired to `setFullTextModal(null)`
// now lives on the caller as `onClose`.

const getInitials = (user) => {
  const firstName =
    user && user.firstName && user.firstName.length > 0 ? user.firstName : 'N'
  const lastName =
    user && user.lastName && user.lastName.length > 0 ? user.lastName : 'N'
  return firstName.charAt(0).toUpperCase() + lastName.charAt(0).toUpperCase()
}

const parseFileNameFromContentDisposition = (contentDisposition) => {
  if (!contentDisposition || typeof contentDisposition !== "string") return "";

  try {
    const utf8Match = contentDisposition.match(/filename\*\s*=\s*UTF-8''([^;]+)/i);
    if (utf8Match && utf8Match[1]) {
      return decodeURIComponent(utf8Match[1].trim().replace(/^"|"$/g, ""));
    }

    const quotedMatch = contentDisposition.match(/filename\s*=\s*"([^"]+)"/i);
    if (quotedMatch && quotedMatch[1]) {
      return quotedMatch[1].trim();
    }

    const plainMatch = contentDisposition.match(/filename\s*=\s*([^;]+)/i);
    if (plainMatch && plainMatch[1]) {
      return plainMatch[1].trim().replace(/^"|"$/g, "");
    }
  } catch (err) {
    return "";
  }

  return "";
};

const getFileNameFromUrl = (fileUrl) => {
  if (!fileUrl || typeof fileUrl !== "string") return "";

  try {
    const baseUrl = typeof window !== "undefined" && window.location ? window.location.origin : "http://localhost";
    const parsedUrl = new URL(fileUrl, baseUrl);

    // S3 signed URLs may carry the real filename in response-content-disposition.
    // Prefer that value because the path/key can be generic or encoded.
    const disposition =
      parsedUrl.searchParams.get("response-content-disposition") ||
      parsedUrl.searchParams.get("ResponseContentDisposition") ||
      "";
    const dispositionFileName = parseFileNameFromContentDisposition(disposition);
    if (dispositionFileName) return dispositionFileName;

    const cleanPath = parsedUrl.pathname || "";
    const parts = cleanPath.split("/");
    return decodeURIComponent(parts[parts.length - 1] || "");
  } catch (err) {
    try {
      const cleanUrl = fileUrl.split("?")[0].split("#")[0];
      const parts = cleanUrl.split("/");
      return decodeURIComponent(parts[parts.length - 1] || "");
    } catch (fallbackErr) {
      return "";
    }
  }
};

const getFileExtension = (fileNameOrUrl) => {
  const rawValue = fileNameOrUrl ? String(fileNameOrUrl) : "";
  const value =
    rawValue.indexOf("?") !== -1 || rawValue.indexOf("/") !== -1
      ? getFileNameFromUrl(rawValue) || rawValue
      : rawValue;
  const cleanValue = value.split("?")[0].split("#")[0];
  const parts = cleanValue.split(".");
  if (parts.length < 2) return "";
  return parts[parts.length - 1].toLowerCase();
};

const formatFileSize = (size) => {
  const bytes = Number(size)
  if (!bytes || Number.isNaN(bytes)) return ""
  if (bytes < 1024) return bytes + " B"
  if (bytes < 1024 * 1024) return Math.round(bytes / 1024) + " KB"
  return (bytes / (1024 * 1024)).toFixed(1) + " MB"
}

const AUDIO_BROWSER_EXTENSIONS = ["mp3", "mpeg", "wav", "m4a", "aac", "ogg", "oga", "opus", "webm"]
const VIDEO_BROWSER_EXTENSIONS = ["mp4", "m4v", "webm", "ogv", "ogg", "mov"]

const getBestPlayableUrlCandidate = (rawAttachment) => {
  if (!rawAttachment || typeof rawAttachment !== "object") return ""
  return (
    rawAttachment.browserPlayableUrl ||
    rawAttachment.playableUrl ||
    rawAttachment.transcodedUrl ||
    rawAttachment.convertedUrl ||
    rawAttachment.previewUrl ||
    rawAttachment.audioPreviewUrl ||
    rawAttachment.videoPreviewUrl ||
    rawAttachment.streamUrl ||
    rawAttachment.streamingUrl ||
    rawAttachment.mp3Url ||
    rawAttachment.m4aUrl ||
    rawAttachment.mp4Url ||
    ""
  )
}

const isBrowserPlayableAudio = (attachment) => {
  if (!attachment) return false
  const mimeType = attachment.mimeType ? String(attachment.mimeType).toLowerCase() : ""
  const extension =
    getFileExtension(attachment.fileName) ||
    getFileExtension(attachment.filename) ||
    getFileExtension(attachment.name) ||
    getFileExtension(attachment.originalName) ||
    getFileExtension(attachment.originalFilename) ||
    getFileExtension(attachment.url) ||
    getFileExtension(attachment.fileUrl) ||
    getFileExtension(attachment.mediaUrl) ||
    getFileExtension(attachment.attachmentUrl) ||
    getFileExtension(attachment.signedUrl) ||
    getFileExtension(attachment.playableUrl) ||
    getFileExtension(attachment.browserPlayableUrl)

  // AMR/AMR-WB commonly arrives from MMS, but native browser audio controls do
  // not reliably play it. It needs a converted MP3/M4A/WAV preview URL.
  if (extension === "amr" || extension === "awb" || mimeType.indexOf("amr") !== -1) {
    return false
  }

  return (
    AUDIO_BROWSER_EXTENSIONS.indexOf(extension) !== -1 ||
    mimeType.indexOf("audio/mpeg") === 0 ||
    mimeType.indexOf("audio/mp3") === 0 ||
    mimeType.indexOf("audio/wav") === 0 ||
    mimeType.indexOf("audio/x-wav") === 0 ||
    mimeType.indexOf("audio/mp4") === 0 ||
    mimeType.indexOf("audio/aac") === 0 ||
    mimeType.indexOf("audio/ogg") === 0 ||
    mimeType.indexOf("audio/webm") === 0
  )
}

const isBrowserPlayableVideo = (attachment) => {
  if (!attachment) return false
  const mimeType = attachment.mimeType ? String(attachment.mimeType).toLowerCase() : ""
  const extension =
    getFileExtension(attachment.fileName) ||
    getFileExtension(attachment.filename) ||
    getFileExtension(attachment.name) ||
    getFileExtension(attachment.originalName) ||
    getFileExtension(attachment.originalFilename) ||
    getFileExtension(attachment.url) ||
    getFileExtension(attachment.fileUrl) ||
    getFileExtension(attachment.mediaUrl) ||
    getFileExtension(attachment.attachmentUrl) ||
    getFileExtension(attachment.signedUrl) ||
    getFileExtension(attachment.playableUrl) ||
    getFileExtension(attachment.browserPlayableUrl)

  return (
    VIDEO_BROWSER_EXTENSIONS.indexOf(extension) !== -1 ||
    mimeType.indexOf("video/mp4") === 0 ||
    mimeType.indexOf("video/webm") === 0 ||
    mimeType.indexOf("video/ogg") === 0 ||
    mimeType.indexOf("video/quicktime") === 0
  )
}

const inferAttachmentKind = (attachment) => {
  if (!attachment) return "file"
  const mimeType = attachment.mimeType ? String(attachment.mimeType).toLowerCase() : ""
  const extension =
    getFileExtension(attachment.fileName) ||
    getFileExtension(attachment.filename) ||
    getFileExtension(attachment.name) ||
    getFileExtension(attachment.originalName) ||
    getFileExtension(attachment.originalFilename) ||
    getFileExtension(attachment.url) ||
    getFileExtension(attachment.fileUrl) ||
    getFileExtension(attachment.mediaUrl) ||
    getFileExtension(attachment.attachmentUrl) ||
    getFileExtension(attachment.signedUrl) ||
    getFileExtension(attachment.playableUrl) ||
    getFileExtension(attachment.browserPlayableUrl)

  if (mimeType.indexOf("image/") === 0 || ["jpg", "jpeg", "png", "gif", "webp", "heic", "heif", "bmp"].indexOf(extension) !== -1) {
    return "image"
  }

  if (mimeType.indexOf("pdf") !== -1 || extension === "pdf") {
    return "pdf"
  }

  if (mimeType.indexOf("audio/") === 0 || ["mp3", "mpeg", "wav", "m4a", "aac", "ogg", "oga", "opus", "amr", "awb", "flac", "webm"].indexOf(extension) !== -1) {
    return "audio"
  }

  if (mimeType.indexOf("video/") === 0 || ["mp4", "mov", "m4v", "webm", "avi", "wmv", "mkv", "3gp", "3g2", "ogv"].indexOf(extension) !== -1) {
    return "video"
  }

  return "file"
}

const normalizeAttachment = (rawAttachment) => {
  if (!rawAttachment) return null

  if (typeof rawAttachment === "string") {
    const urlValue = rawAttachment
    const fileNameValue = getFileNameFromUrl(urlValue)
    const normalizedStringAttachment = {
      url: urlValue,
      fileName: fileNameValue,
      mimeType: "",
      fileSize: null,
      playableUrl: "",
    }
    const kind = inferAttachmentKind(normalizedStringAttachment)
    return {
      ...normalizedStringAttachment,
      kind,
      playableUrl:
        kind === "audio" && isBrowserPlayableAudio(normalizedStringAttachment)
          ? urlValue
          : kind === "video" && isBrowserPlayableVideo(normalizedStringAttachment)
            ? urlValue
            : "",
    }
  }

  if (typeof rawAttachment !== "object") return null

  const urlValue =
    rawAttachment.url ||
    rawAttachment.fileUrl ||
    rawAttachment.mediaUrl ||
    rawAttachment.attachmentUrl ||
    rawAttachment.signedUrl ||
    rawAttachment.secureUrl ||
    rawAttachment.location ||
    rawAttachment.href ||
    ""

  const playableUrlValue = getBestPlayableUrlCandidate(rawAttachment)

  const fileNameValue =
    rawAttachment.fileName ||
    rawAttachment.filename ||
    rawAttachment.name ||
    rawAttachment.originalName ||
    rawAttachment.originalFilename ||
    rawAttachment.key ||
    getFileNameFromUrl(urlValue) ||
    getFileNameFromUrl(playableUrlValue) ||
    "Attachment"

  const normalizedObjectAttachment = {
    url: urlValue,
    fileName: fileNameValue,
    mimeType:
      rawAttachment.mimeType ||
      rawAttachment.contentType ||
      rawAttachment.mediaContentType ||
      rawAttachment.type ||
      "",
    fileSize:
      rawAttachment.fileSize ||
      rawAttachment.size ||
      rawAttachment.bytes ||
      rawAttachment.contentLength ||
      null,
    playableUrl: playableUrlValue,
  }

  const kind = inferAttachmentKind(normalizedObjectAttachment)
  const playableCandidate = normalizedObjectAttachment.playableUrl

  return {
    ...normalizedObjectAttachment,
    kind,
    playableUrl:
      playableCandidate ||
      (kind === "audio" && isBrowserPlayableAudio(normalizedObjectAttachment)
        ? urlValue
        : kind === "video" && isBrowserPlayableVideo(normalizedObjectAttachment)
          ? urlValue
          : ""),
    needsConversion:
      (kind === "audio" && !playableCandidate && !isBrowserPlayableAudio(normalizedObjectAttachment)) ||
      (kind === "video" && !playableCandidate && !isBrowserPlayableVideo(normalizedObjectAttachment)),
  }
}

const appendAttachmentCandidates = (candidates, value) => {
  if (!value) return
  if (Array.isArray(value)) {
    value.forEach((item) => appendAttachmentCandidates(candidates, item))
    return
  }
  candidates.push(value)
}

const getFirstMessagingAttachment = (result) => {
  if (!result) return null

  const messagingResponse = result.messagingResponse || {}
  const candidates = []

  appendAttachmentCandidates(candidates, messagingResponse.attachments)
  appendAttachmentCandidates(candidates, messagingResponse.attachment)
  appendAttachmentCandidates(candidates, messagingResponse.media)
  appendAttachmentCandidates(candidates, messagingResponse.mediaFile)
  appendAttachmentCandidates(candidates, messagingResponse.mediaFiles)
  appendAttachmentCandidates(candidates, messagingResponse.files)
  appendAttachmentCandidates(candidates, messagingResponse.file)
  appendAttachmentCandidates(candidates, messagingResponse.mediaUrl)
  appendAttachmentCandidates(candidates, messagingResponse.mediaUrls)
  appendAttachmentCandidates(candidates, messagingResponse.fileUrl)
  appendAttachmentCandidates(candidates, messagingResponse.attachmentUrl)
  appendAttachmentCandidates(candidates, messagingResponse.signedUrl)

  appendAttachmentCandidates(candidates, result.attachment)
  appendAttachmentCandidates(candidates, result.attachments)
  appendAttachmentCandidates(candidates, result.media)
  appendAttachmentCandidates(candidates, result.mediaFile)
  appendAttachmentCandidates(candidates, result.mediaFiles)
  appendAttachmentCandidates(candidates, result.files)
  appendAttachmentCandidates(candidates, result.file)
  appendAttachmentCandidates(candidates, result.mediaUrl)
  appendAttachmentCandidates(candidates, result.mediaUrls)
  appendAttachmentCandidates(candidates, result.fileUrl)
  appendAttachmentCandidates(candidates, result.attachmentUrl)
  appendAttachmentCandidates(candidates, result.signedUrl)

  for (let i = 0; i < candidates.length; i++) {
    const normalized = normalizeAttachment(candidates[i])
    if (normalized && (normalized.url || normalized.fileName)) {
      return normalized
    }
  }

  return null
}

const getAttachmentTypeLabel = (attachment) => {
  if (!attachment) return 'Attachment'
  if (attachment.kind === 'image') return 'Image attachment'
  if (attachment.kind === 'pdf') return 'PDF attachment'
  if (attachment.kind === 'audio') return 'Audio attachment'
  if (attachment.kind === 'video') return 'Video attachment'
  return 'File attachment'
}

const getAttachmentActionText = (attachment) => {
  if (!attachment) return 'View file'
  if (attachment.kind === 'image') return 'View full image'
  if (attachment.kind === 'pdf') return 'View PDF'
  if (attachment.kind === 'audio') return attachment.playableUrl ? 'Open audio' : 'Open/download audio'
  if (attachment.kind === 'video') return attachment.playableUrl ? 'Open video' : 'Open/download video'
  return 'View file'
}

const renderAttachmentSummary = (rawAttachment) => {
  const attachment = normalizeAttachment(rawAttachment)
  if (!attachment) return null

  const sizeText = formatFileSize(attachment.fileSize)
  const actionText = getAttachmentActionText(attachment)
  const actionUrl =
    attachment.kind === 'audio' || attachment.kind === 'video'
      ? attachment.playableUrl || attachment.url
      : attachment.url || attachment.playableUrl

  return (
    <div className="bwai-attach-summary">
      <div className="bwai-response-eyebrow">
        Attachment
      </div>
      <div className="bwai-attach-summary-card">
        <div className="bwai-attach-summary-main">
          <div
            className={
              attachment.kind === 'pdf'
                ? 'bwai-attach-summary-thumb bwai-attach-summary-thumb--pdf'
                : 'bwai-attach-summary-thumb'
            }
          >
            {attachment.kind === 'image' && attachment.url ? (
              <img
                src={attachment.url}
                alt={attachment.fileName || 'Attachment'}
                className="bwai-attach-summary-img"
                onError={(event) => {
                  event.currentTarget.style.display = 'none'
                }}
              />
            ) : attachment.kind === 'pdf' ? (
              <BsFileEarmarkPdf size={20} />
            ) : (
              <BsPaperclip size={20} />
            )}
          </div>
          <div className="bwai-attach-summary-text">
            <div className="bwai-attach-summary-name">
              {attachment.fileName || getAttachmentTypeLabel(attachment)}
            </div>
            <div className="bwai-attach-summary-sub">
              {getAttachmentTypeLabel(attachment)}{sizeText ? ' · ' + sizeText : ''}
            </div>
            {attachment.needsConversion ? (
              <div className="bwai-attach-summary-note">
                Preview unavailable for this format.
              </div>
            ) : null}
          </div>
        </div>

        {actionUrl ? (
          <a
            href={actionUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="bwai-attach-summary-action"
            data-html2canvas-ignore="true"
          >
            {actionText} <BsBoxArrowUpRight size={12} />
          </a>
        ) : null}
      </div>
    </div>
  )
}

const MessagingResponseFullModal = ({ data, onClose, popoverIdSuffix, user, adminAId }) => {
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false)
  // Resolved by TriviaSMSResponses once the full chat thread is rendered.
  const threadReadyRef = useRef(null)

  if (!data) return null

  const attachment = data.attachment || getFirstMessagingAttachment(data)

  const handleThreadReadyForPdf = () => {
    if (threadReadyRef.current) {
      threadReadyRef.current()
      threadReadyRef.current = null
    }
  }

  const handleDownloadPdf = async (e) => {
    const modalContent = e.currentTarget.closest('.modal-content')
    if (!modalContent) return

    const userName =
      data.user && !data.isAnonymous
        ? `${data.user.firstName || ''} ${data.user.lastName || ''}`.trim()
        : 'Anonymous user'

    // TriviaSMSResponses renders nothing for non-messaging data, so there is
    // no thread to wait for.
    const threadReady = data.isMessaging
      ? new Promise((resolve) => {
          threadReadyRef.current = resolve
        })
      : Promise.resolve()

    setIsGeneratingPdf(true)
    try {
      await threadReady
      // Let the expanded thread paint before capturing it.
      await new Promise((resolve) => requestAnimationFrame(resolve))
      await downloadElementAsPdf(
        modalContent,
        toPdfFileName(`${userName} Response`)
      )
    } catch (err) {
      console.error('Error generating response PDF', err)
    } finally {
      threadReadyRef.current = null
      setIsGeneratingPdf(false)
    }
  }

  // Allow callers to suffix the OoPopover target id to avoid DOM collisions
  // when this modal can be mounted from multiple call sites in the same
  // session. Defaults to the legacy id to preserve any existing CSS / tests.
  const popoverTarget = popoverIdSuffix
    ? 'messaging_users_full_response_modal_' + popoverIdSuffix
    : 'messaging_users_full_response_modal'

  return (
    <Modal
      isOpen={true}
      size="lg"
      className="messaging_response_full_modal bwai-response-modal"
    >
      <ModalHeader
        className="trivia_modal_header_bg response-modal-header-with-download"
        toggle={onClose}
      >
        <div className="d-flex align-items-center justify-content-between">
        <div>
        {data.user && !data.isAnonymous ? (
          <span className="trivia_table_text_wrapper">
            {profilePicValidityCheck(data.user.profilePic) ? (
              <img
                src={data.user.profilePic}
                height="35px"
                width="35px"
                className="rounded-circle"
                alt=""
              />
            ) : (
              <span
                className="spotlightUserInitials"
                data-letters={getInitials(data.user)}
              ></span>
            )}{' '}
            <span className="bwai-response-name">
              {' '}
              {data.user.firstName} {data.user.lastName}
            </span>
          </span>
        ) : (
          <span className="trivia_table_text_wrapper">
            <span
              className="spotlightUserInitials"
              data-letters={'AU'}
            ></span>
            <span className="bwai-response-name">Anonymous user</span>{' '}
          </span>
        )}{' '}
        Response{' '}
        <OoPopover
          target={popoverTarget}
          placement="bottom"
          css_class="user_info_popover"
          headerText="User Info"
          bodyText={
            <div className="messaging_users_full_response_modal_userInfo">
              {data.user && data.user.mobileNumber ? (
                <div>
                  {' '}
                  <b>Mobile Number:</b>
                  <div>{data.user.mobileNumber}</div>
                </div>
              ) : (
                ''
              )}
              {data.user && data.user.email ? (
                <div className={data.user.mobileNumber ? 'mt-3' : ''}>
                  {' '}
                  <b>Email:</b>
                  <div> {data.user.email}</div>
                </div>
              ) : (
                ''
              )}
            </div>
          }
        />
        </div>
        <button
          type="button"
          className="btn response-modal-download-btn"
          onClick={handleDownloadPdf}
          disabled={isGeneratingPdf}
          data-html2canvas-ignore="true"
        >
          <BsDownload size={15} />
          {isGeneratingPdf ? 'Generating PDF...' : 'Download'}
        </button>
        </div>
      </ModalHeader>
      <ModalBody className="p-3">
        <div className="my-3">
          <b>Submitted on:</b>{' '}
          {data.date ? moment(data.date).format('lll') : <ListEmptyDash />}
        </div>
        {data.sentimentData && (
          <div className="my-3 responses-modal-sentiment-analysis-container">
            <b>Sentiment:</b>{' '}
            {data.sentimentData.sentiment == 'POSITIVE' ? (
              <span className="sentiment-analysis_positive fw600">
                <BsEmojiSmile />
                {SENTIMENTS_TYPO_MAP2[data.sentimentData.sentiment]}
              </span>
            ) : data.sentimentData.sentiment == 'NEGATIVE' ? (
              <span className="sentiment-analysis_negative fw600">
                <BsEmojiFrown />:
                {SENTIMENTS_TYPO_MAP2[data.sentimentData.sentiment]}
              </span>
            ) : data.sentimentData.sentiment == 'MIXED' ? (
              <span className="sentiment-analysis_mixed fw600">
                <FaRegFaceMehBlank />
                {SENTIMENTS_TYPO_MAP2[data.sentimentData.sentiment]}
              </span>
            ) : data.sentimentData.sentiment == 'NEUTRAL' ? (
              <span className="sentiment-analysis_neutral fw600">
                <BsEmojiNeutral />
                {SENTIMENTS_TYPO_MAP2[data.sentimentData.sentiment]}
              </span>
            ) : (
              ''
            )}
          </div>
        )}
        <p className="my-3">
          {' '}
          <b>Response: </b> {data.response}
        </p>

        {renderAttachmentSummary(attachment)}

        {/* The transcript stays original; only the "Response:" line above is translated. */}
        <TriviaSMSResponses
          trivia={{ ...data, response: data.originalResponse || data.response, attachment }}
          user={user}
          adminAId={adminAId}
          isExportingPdf={isGeneratingPdf}
          onExportReady={handleThreadReadyForPdf}
        />
      </ModalBody>
    </Modal>
  )
}

export default MessagingResponseFullModal
